
import { useState, useEffect } from "react"

// Define standard breakpoints for responsive design
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
}

/**
 * Hook to detect if the current viewport is mobile-sized
 * @returns boolean indicating if the viewport is mobile-sized
 */
export function useIsMobile() {
  // Initialize with false to prevent hydration issues
  const [isMobile, setIsMobile] = useState<boolean>(false)

  useEffect(() => {
    // Define the media query
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`)
    
    const onChange = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.md)
    }
    
    // Set initial value immediately
    setIsMobile(window.innerWidth < BREAKPOINTS.md)
    
    // Add event listener
    mql.addEventListener("change", onChange)
    
    // Cleanup on unmount
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isMobile
}

/**
 * Hook to detect current screen breakpoint
 * @returns Current breakpoint: 'sm', 'md', 'lg', 'xl', or '2xl'
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<string>("sm")

  useEffect(() => {
    const determineBreakpoint = () => {
      const width = window.innerWidth
      
      if (width < BREAKPOINTS.sm) return "xs"
      if (width < BREAKPOINTS.md) return "sm"
      if (width < BREAKPOINTS.lg) return "md"
      if (width < BREAKPOINTS.xl) return "lg"
      if (width < BREAKPOINTS["2xl"]) return "xl"
      return "2xl"
    }

    const handleResize = () => {
      setBreakpoint(determineBreakpoint())
    }

    // Set initial value
    handleResize()
    
    // Add event listener
    window.addEventListener("resize", handleResize)
    
    // Cleanup
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return breakpoint
}
